import { Component, ElementRef, HostBinding, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import {
  MainService,
  AuthService,
  NotificationsService,
} from "@services";
import { RoutesApp } from "../constants/Routes.constants";
import { UneteIO } from '../backDebugger'
import { LanguageSelectComponent } from "../../modules/auth/components/language-select/language-select.component";
import { TranslatePipe } from "../../modules/auth/i18n/translate.pipe";
import { I18nService } from "../../modules/auth/i18n/i18n.service";
import { ToastService } from "../../modules/primitives/toasts/toast.service";
import { FormsModule } from "@angular/forms";
import { ButtonComponent } from "../components/button/button.component";
import { UserService } from "../user.service";
import { DataValidations } from "../@utils/DataValidations";

import { Subscription } from "rxjs";

@Component({
  selector: "app-login",
  styleUrls: ["./login.style.scss"],
  templateUrl: "./login.template.html",
  standalone: true,
  imports: [
    LanguageSelectComponent,
    TranslatePipe,
    FormsModule,
    ButtonComponent,
    RouterLink
]
})
export class LoginComponent implements OnInit {
  readonly rememberMeEnabled = true;
  /** Los campos no se marcan hasta el primer intento de envío. */
  attempted = false;
  rememberMe = false;
  @HostBinding("class") classes = "login-page app";
  @ViewChild("emailInput") emailInput?: ElementRef<HTMLInputElement>;
  @ViewChild("passwordInput") passwordInput?: ElementRef<HTMLInputElement>;

  inspector = UneteIO.inspector;
  user: User = {
    email: "",
    password: "",
  };
  userLogged: any;
  token;
  tokenInfo;
  tokenReset;
  userRe: any = undefined;
  passRe;
  newPass;
  loginLoading = false;
  showPassword = false;
  public mxn: any;
  private listeners: Subscription[] = [];

  constructor(
    private ms: MainService,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private notificationsService: NotificationsService,
    private userService: UserService,
    private toasts: ToastService,
    private i18n: I18nService
  ) { }

  async ngOnInit() {
    this.setupListeners();
    this.getToken();

    if (sessionStorage.getItem("tokenRe")) {
      this.loadIfHaveTokenRe();
    }
  }
  
  ngOnDestroy() {
    this.removeListeners();
  }

  setupListeners () {
    // NotDashboardGuard handles redirecting already-authenticated users.
    // Navigating here caused a race condition: the onValue fired before localStorage.user
    // was populated by UserService.syncUser(), crashing SidebarComponent on first login.
  }

  removeListeners () {

  }

  /**
   * `null` mientras el usuario no ha intentado enviar: nada de rojo prematuro.
   * Después se reevalúa en cada pulsación, así que el campo se limpia solo en
   * cuanto es válido, sin tener que reenviar.
   */
  fieldError(field: 'email' | 'password'): 'required' | 'invalid-email' | null {
    if (!this.attempted) return null;

    const value = (this.user[field] ?? '').trim();
    if (!value) return 'required';
    if (field === 'email' && DataValidations.email({ value } as any)) return 'invalid-email';

    return null;
  }

  private focusFirstInvalid() {
    const target = this.fieldError('email') ? this.emailInput : this.passwordInput;
    target?.nativeElement.focus();
  }

  private syncLoginInputs() {
    const email = this.emailInput?.nativeElement.value;
    const password = this.passwordInput?.nativeElement.value;

    if (email !== undefined) this.user.email = email;
    if (password !== undefined) this.user.password = password;
  }

  async loadIfHaveTokenRe() {
    this.tokenReset = sessionStorage.getItem("tokenRe");
    this.userRe = await this.ms.getDecodedAccessToken(
      sessionStorage.getItem("tokenRe")
    );
  }

  getToken() {
    this.token = this.route.snapshot.queryParams;

    if (this.token.bankAccount) {
      this.authService.setLocalItem("willRedirectToAccount", "true");
    }

    if ((this.token && this.token.token) || this.tokenReset) {
      this.tokenInfo = this.ms.getDecodedAccessToken(this.token.token);
      this.authService.setLocalJson("onBoardUser", this.tokenInfo);
    }

    if (this.token.shipmentId) {
      this.authService.setLocalJson(
        "onBoardUser",
        { id: this.token.shipmentId, token: this.token.token }
      );
    }
  }

  async login(actualLogin?: any, ignoreFields?) {
    if (this.loginLoading) return;
    this.loginLoading = true;
    this.syncLoginInputs();
    this.inspector.start();
    ignoreFields = false;
    this.showPassword = false;
    this.attempted = true;

    // Los campos inválidos se señalan en el propio control, no con un toast: el
    // inline dice cuál falla y se limpia solo conforme el usuario escribe.
    if (!ignoreFields && (this.fieldError('email') || this.fieldError('password'))) {
      this.loginLoading = false;
      this.focusFirstInvalid();
    } else {
      // Convert to lowercase to ensure user doesnt make a mistake
      this.user.email = this.user.email.trim();
      try {
        const res = actualLogin || (await this.authService.login(this.user.email, this.user.password));
        const user = res.user;

        await this.authService.token.sync(res.token);
        await this.userService.user.sync();

        this.authService.setLocalItem("showNextSteps", "true");

        this.ms.setLocalValue("user", { ...user });
        this.authService.setLocalItem("nav-static", "true");
        this.notificationsService.setupListeners()

        //* If the user is a driver, prevent it to login
        if (user?.role === "driver") {
          this.ms.clear();
          this.loginLoading = false;
          return this.toasts.error(
            this.i18n.t('toast.roleNotAuthorized'),
            this.i18n.t('toast.roleNotAuthorizedDetail')
          );
        }

        //* If there is a redirection url, redirect to it
        if (sessionStorage.getItem("homeUrl")) {
          location.href = sessionStorage.getItem("homeUrl");
        }

        const queryParams = JSON.parse(this.authService.getLocalItem("shipmentListFilters") || "{}");

        if (queryParams && queryParams.c === user?.companyId) {
          this.authService.removeLocalItem("shipmentListFilters");
          this.router.navigate([RoutesApp.Shipment.Drayage.List], {
            queryParams,
          });
        } else if (
          this.tokenInfo != undefined &&
          this.tokenInfo.userId === this.authService.getLocalItem("userId")
        ) {
          this.authService.removeLocalItem("shipmentListFilters");
          this.router.navigate([this.ms.cts.getRoutes()[1].url]);
        } else if (user.customerOf) {
          this.router.navigate([RoutesApp.Shipment.Drayage.My]);
        } else {
          this.authService.removeLocalItem("shipmentListFilters");
          setTimeout(() => {
            const userRoles = this.ms.cts.getRoles();

            if ((user?.role === userRoles?.shipper) || (user?.role === userRoles?.broker)) {
              this.router.navigate([RoutesApp.Shipment.Drayage.Create]);
            } else {
              this.router.navigate([RoutesApp.Shipment.Drayage.List]);
            }
          });
        }
        sessionStorage.removeItem("homeUrl");
        this.ms.listener.dispatchEvent("setObservables@loadingComponent"); //dispatch event to set observables
        this.ms.getMxnValue().subscribe({
          next: resp => {
            this.mxn = resp
            this.authService.setLocalItem('mxnValue', this.mxn.result)
          },
          error: () => { }
        });

        this.inspector.end('Login')
        // El spinner se mantiene activo hasta que la navegación destruye este
        // componente; apagarlo aquí lo cortaba antes del cambio de vista.
      } catch (exc) {
        // El detalle viene del backend (p. ej. "Unauthorized"); el título es
        // nuestro y traducido.
        this.toasts.error(this.i18n.t('toast.loginFailed'), exc?.message ?? String(exc));
        this.loginLoading = false;
      }
    }
  }

}

interface User {
  email: string;
  password: string;
}
